import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import OtpModal from "../Modals/OtpModal";
import WelcomeModal from "../Modals/welcomeModal/WelcomeModal";
import EventRegistrationSuccessModal from "../Modals/eventRegistrationSuccessModal/EventRegistrationSuccessModal";
import PasswordSentModal from "../Modals/passwordSentModal/PasswordSentModal";
import "./SignUp.css";
import axios from "axios";
import { useAuth, AuthContext } from "../context/auth";
import { addGuestToEvent } from "../helper/addGuestToEvent";
import { addUsherToEvent } from "../helper/addUsherToEvent";
import { toast } from "react-toastify";
import {
  getPaidEventSession,
  clearPaidEventSession,
  transferToPostSignupInterest,
  cleanupOnPageLoad,
} from "../helper/paidEventSignupSession";

const SignUp = ({ onSubmit, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const params = new URLSearchParams(location.search);
  const signUpRole = params.get("role");

  const {
    role: stateRole,
    inviteCode: stateInviteCode,
    hostId: stateHostId,
    from: fromState,
  } = location.state || {};

  const selectedRole = stateRole || signUpRole;
  const inviteCode = stateInviteCode;
  const hostId = stateHostId;

  const [loading, setLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const logoImage2 = "/images/logoOnBlack.png";

  const [paidEventData, setPaidEventData] = useState(null);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [registeredEventData, setRegisteredEventData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: selectedRole || "guest",
  });

  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    password: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    password: false,
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [userIdForOtpModal, setUserIdForOtpModal] = useState("");
  const { user, setUser, authenticate } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [countryCode, setCountryCode] = useState("+234");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [welcomeModalProps, setWelcomeModalProps] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    oneSpecialChar: false,
    oneNumber: false,
    oneUppercase: false,
    minLength: false,
    oneLowercase: false,
  });

  const [showDefaultPasswordModal, setShowDefaultPasswordModal] =
    useState(false);
  const [defaultPasswordEmail, setDefaultPasswordEmail] = useState("");

  const [showPasswordSentModal, setShowPasswordSentModal] = useState(false);

  useEffect(() => {
    cleanupOnPageLoad();
    const sessionData = getPaidEventSession();

    if (sessionData) {
      setPaidEventData(sessionData);
    } else {
      setPaidEventData(null);
    }

    return () => {
      const currentSession = getPaidEventSession();
      if (currentSession && !userData?.isVerified) {
        clearPaidEventSession();
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentSession = getPaidEventSession();
      if (currentSession && !userData?.isVerified) {
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [userData]);

  const persistUser = (token, userInfo) => {
    if (!userInfo?._id) return;

    if (token) {
      authenticate(token, userInfo);
    } else {
      setUser(userInfo);
    }
    localStorage.setItem("userInfo", JSON.stringify(userInfo));
  };

  const isEmailValid = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const areAllRequirementsMet = useCallback(() => {
    return Object.values(passwordRequirements).every(
      (requirement) => requirement === true,
    );
  }, [passwordRequirements]);

  const updatePasswordRequirements = useCallback((text) => {
    const newRequirements = {
      oneSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(text),
      oneNumber: /[0-9]/.test(text),
      oneUppercase: /[A-Z]/.test(text),
      minLength: text.length >= 8,
      oneLowercase: /[a-z]/.test(text),
    };
    setPasswordRequirements(newRequirements);
  }, []);

  const validateField = useCallback(
    (field, value) => {
      let hasError = false;
      switch (field) {
        case "firstName":
          hasError = !value.trim();
          break;
        case "lastName":
          hasError = !value.trim();
          break;
        case "email":
          hasError = !value.trim() || !isEmailValid(value);
          break;
        case "phoneNumber":
          hasError = !value.trim();
          break;
        case "password":
          hasError = !value.trim() || !areAllRequirementsMet();
          break;
        default:
          break;
      }
      setFormErrors((prev) => ({ ...prev, [field]: hasError }));
    },
    [isEmailValid, areAllRequirementsMet],
  );

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);

    if (digits.length <= 3) return part1;
    if (digits.length <= 6) return `${part1} ${part2}`;
    return `${part1} ${part2} ${part3}`;
  };

  const handleInputChange = useCallback(
    (field, value) => {
      if (field === "phoneNumber") {
        value = formatPhoneNumber(value);
      }

      setFormData((prevState) => ({ ...prevState, [field]: value }));

      if (field === "password") {
        updatePasswordRequirements(value);
      }

      validateField(field, value);
    },
    [updatePasswordRequirements, validateField],
  );

  const handleFieldFocus = useCallback((field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    if (field === "password") {
      setPasswordFocused(true);
    }
  }, []);

  const handleFieldBlur = useCallback(
    (field) => {
      if (field === "password" && formData.password === "") {
        setPasswordFocused(false);
      }
    },
    [formData.password],
  );

  const checkFormValidity = useCallback(() => {
    const noEmptyFields = Object.values(formData).every(
      (value) => value?.trim() !== "",
    );
    const validEmail = isEmailValid(formData.email);
    const validPassword = areAllRequirementsMet();
    return noEmptyFields && validEmail && validPassword;
  }, [formData, isEmailValid, areAllRequirementsMet]);

  useEffect(() => {
    const isValid = checkFormValidity();
    setIsFormValid(isValid);
  }, [formData, checkFormValidity]);

  const showAlert = (title, message) => {
    alert(`${title}: ${message}`);
  };

  const handleShowDefaultPasswordModal = (email) => {
    setDefaultPasswordEmail(email);
    setShowDefaultPasswordModal(true);
  };

  const handleSendDefaultPassword = async () => {
    try {
      setIsLoading(true);
      setShowDefaultPasswordModal(false);

      const response = await axios.post("/user/send-default-password", {
        email: defaultPasswordEmail.toLowerCase().trim(),
      });

      if (response.data.success) {
        setShowPasswordSentModal(true);
      }
    } catch (error) {
      showAlert(
        "Error",
        error.response?.data?.message ||
          "Failed to send password. Please try again.",
      );
    } finally {
      setIsLoading(false);
      setDefaultPasswordEmail("");
    }
  };

  const handleCancelDefaultPassword = () => {
    setShowDefaultPasswordModal(false);
    setDefaultPasswordEmail("");
    setFormData({ ...formData, email: "" });
  };

  const handleOtpVerify = useCallback(
    async (code, method) => {
      setIsVerifying(true);
      try {
        if (code.length !== 6) {
          setIsVerifying(false);
          return;
        }
        if (method === "sms") {
          await verifyPhoneOtp(code);
        } else {
          await verifyEmailOtp(code);
        }
      } catch (error) {
        showAlert(
          "Verification Failed",
          error.message || "Verification failed",
        );
        setIsVerifying(false);
      }
    },
    [
      formData.email,
      formData.phoneNumber,
      phoneNumber,
      userIdForOtpModal,
      selectedRole,
    ],
  );

  const verifyPhoneOtp = async (code) => {
    setIsVerifying(true);
    try {
      const response = await axios.post("/sms/otp/verify-sms", {
        phoneNumber: formData.phoneNumber,
        smsOtp: code,
        userId: userIdForOtpModal,
      });
      if (!response.data.successStatus) {
        showAlert("Verification Failed", response.data.message);
        return;
      }
      setUserData((prevData) => ({
        ...prevData,
        _id: response.data.data.user._id,
        isPhoneVerified: true,
      }));

      setIsOtpModalOpen(false);
      showAlert("Verification Successful", "Phone number verified");
      if (userData && userData.token) {
        authenticate(userData.token, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          role: [selectedRole],
          isVerified: true,
          _id: userIdForOtpModal,
        });
      }
      navigate("/profile-picture", {
        state: {
          userData: {
            ...formData,
            _id: userIdForOtpModal,
          },
          inviteCode,
          hostId,
          source: "ONBOARDING",
        },
      });
    } catch (err) {
      showAlert(
        "Verification Failed",
        err.response?.data?.message || "Error verifying OTP",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const fullName = `${userData?.firstName || user?.firstName || ""} ${
    userData?.lastName || user?.lastName || ""
  }`.trim();

  const userInfo = {
    userId: userData?.userId || user?.userId || userData?._id || "",
    firstName:
      formData.firstName || userData?.firstName || user?.firstName || "",
    lastName: formData.lastName || userData?.lastName || user?.lastName || "",
    email: formData.email || userData?.email || user?.email || "",
    phoneNumber:
      formData.phoneNumber || userData?.phoneNumber || user?.phoneNumber || "",
    role: [selectedRole],
  };

  const handleWelcomeComplete = async (action) => {
    setIsProcessing(true);

    const emailToUse = formData?.email || userData?.email || user?.email || "";
    const passwordToUse = formData?.password || userData?.password || "";

    try {
      const loginResponse = await axios.post("/user/login", {
        email: emailToUse,
        password: passwordToUse,
      });

      const {
        user: loggedInUser,
        token,
        refreshToken,
      } = loginResponse?.data?.data;

      const freshUser = { ...loggedInUser, activeRole: selectedRole };

      authCtx.authenticate(token, freshUser, refreshToken);
      authCtx.setUser((prevUser) => ({ ...prevUser, ...freshUser }));

      localStorage.setItem("authToken", token);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("userData", JSON.stringify(freshUser));

      if (paidEventData?.eventId) {
        try {
          const response = await axios.put(
            `/event/${paidEventData.eventId}/invitation/respond`,
            {
              guestId: freshUser._id,
              status: "accepted",
              isPublicEventInterest: true,
              isPostSignupInterest: true,
              numberOfGuests: 1,
            },
          );

          clearPaidEventSession();
          setPaidEventData(null);

          setIsWelcomeModalOpen(false);
          setIsProcessing(false);

          setRegisteredEventData({
            eventId: paidEventData.eventId,
            eventTitle: paidEventData.eventTitle,
            freshUser: freshUser,
          });
          setIsSuccessModalOpen(true);

          return;
        } catch (apiError) {
          clearPaidEventSession();
          setPaidEventData(null);
        }
      }

      const fullName =
        `${freshUser.firstName || ""} ${freshUser.lastName || ""}`.trim();

      if (action === "createEvent") {
        navigate("/home/host", {
          state: { userName: fullName, user: freshUser },
        });
      } else if (action === "joinEvent" && freshUser.activeRole === "guest") {
        try {
          setLoading(true);
          const guestResponse = await addGuestToEvent(
            inviteCode,
            hostId,
            freshUser._id,
          );
          if (guestResponse.error) {
            showAlert(
              "Event Join Failed!",
              guestResponse.isAlreadyInvited
                ? "You are already invited to this event"
                : guestResponse.message || "Failed to add guest to event",
            );
          } else {
            setLoading(false);
            navigate("/invites?reload=true", {
              state: {
                userName: fullName,
                user: freshUser,
                inviteCode,
                hostId,
              },
            });
            localStorage.setItem("inviteUser", JSON.stringify(freshUser));
          }
        } catch (err) {
          showAlert("Event Join Failed!", "Failed to add guest to event");
        }
      } else if (action === "joinEvent" && freshUser.activeRole === "usher") {
        try {
          setLoading(true);
          const usherResponse = await addUsherToEvent(
            inviteCode,
            hostId,
            freshUser._id,
          );
          if (usherResponse.error) {
            showAlert(
              "Usher Addition Failed!",
              usherResponse.isAlreadyInvited
                ? "You are already invited to this event"
                : usherResponse.message || "Failed to add usher to event",
            );
          } else {
            setLoading(false);
            navigate("/invites?reload=true", {
              state: {
                userName: fullName,
                user: freshUser,
                inviteCode,
                hostId,
              },
            });
            localStorage.setItem("inviteUser", JSON.stringify(freshUser));
          }
        } catch (err) {
          showAlert("Usher Addition Failed!", "Failed to add usher to event");
        }
      } else {
        if (freshUser.activeRole === "guest") {
          navigate("/home/guest", {
            state: { userName: fullName, user: freshUser },
          });
        } else if (freshUser.activeRole === "usher") {
          navigate("/home/usher", {
            state: { userName: fullName, user: freshUser },
          });
        } else {
          navigate("/home/host", {
            state: { userName: fullName, user: freshUser },
          });
        }
      }

      setIsWelcomeModalOpen(false);
    } catch (error) {
      showAlert("Login Failed", "Please log in manually.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setIsWelcomeModalOpen(false);
    }
  };

  const handleViewEvent = () => {
    setIsSuccessModalOpen(false);
    navigate(`/eventManagementDetails/${registeredEventData.eventId}`, {
      state: {
        fromWelcome: "true",
        userName: `${registeredEventData.freshUser.firstName} ${registeredEventData.freshUser.lastName}`,
        user: registeredEventData.freshUser,
      },
    });
  };

  const handleExploreEvents = () => {
    setIsSuccessModalOpen(false);
    navigate("/home/guest", {
      state: {
        userName: `${registeredEventData.freshUser.firstName} ${registeredEventData.freshUser.lastName}`,
        user: registeredEventData.freshUser,
      },
    });
  };

  const welcomeModalProp = {
    isOpen: true,
    userName: `${formData.firstName} ${formData.lastName}`,
    message:
      "Take a deep breath and relax — welcome to NimbleBash! 🎉 Thank you for joining us. Let's get started planning your next unforgettable party or celebration!",
    buttons: {
      primary: "Get Started",
      primaryAction: () => navigate("/dashboard"),
      secondary: "Explore",
      secondaryAction: () => navigate("/explore"),
    },
    inviteCode: null,
    hostId: null,
    role: "guest",
    userData: { ...formData, _id: userIdForOtpModal },
  };

  const userName = `${userData?.user?.firstName || user?.firstName || ""} ${
    userData?.user?.lastName || user?.lastName || ""
  }`.trim();

  const verifyEmailOtp = async (code) => {
    setIsVerifying(true);
    setInviteError("");

    try {
      if (!code || code.length < 4) {
        showAlert("Invalid Code", "Please enter a valid verification code.");
        throw new Error("Invalid code");
      }

      const encodedEmail = encodeURIComponent(formData.email);

      const response = await axios.get(`/user/verify/${encodedEmail}/${code}`, {
        validateStatus: () => true,
      });

      if (response.status !== 200 || !response.data?.successStatus) {
        const errorMessage =
          response.data?.message || "Invalid verification code.";

        showAlert("Verification Failed", errorMessage);
        setInviteError(errorMessage);
        throw new Error(errorMessage);
      }

      const user = response.data.data.user;
      setUserData((prevData) => ({
        ...prevData,
        _id: user._id,
        isVerified: true,
        user,
      }));

      if (response.data.data.shouldLogin) {
        showAlert(
          "Verification Successful",
          "Your email has been verified. Please login to continue.",
        );

        if (response.data.data?.token) {
          persistUser(response.data.data.token, {
            ...formData,
            role: [selectedRole],
            isVerified: true,
            _id: userIdForOtpModal,
          });
        }

        return "success";
      }

      toast.success("Email verified successfully!");

      if (response.data.data?.token) {
        authenticate(response.data.data.token, {
          ...formData,
          role: [selectedRole],
          isVerified: true,
          _id: userIdForOtpModal,
        });
      }

      if (selectedRole === "guest" || selectedRole === "usher") {
        if (paidEventData?.eventId) {
          const pendingInterest = transferToPostSignupInterest();
        }

        setIsOtpModalOpen(false);
        setIsWelcomeModalOpen(true);
        setWelcomeModalProps(welcomeModalProp);
        return "success";
      }

      navigate("/profile-picture", {
        state: {
          userData: { ...formData, _id: userIdForOtpModal },
          inviteCode,
          hostId,
          source: "ONBOARDING",
        },
      });

      return "success";
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Verification failed. Please check the code and try again.";

      showAlert("Verification Failed", errorMessage);
      setInviteError(err.message || "Invalid invite code");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async (method) => {
    try {
      if (method === "email") {
        if (!formData.email) {
          throw new Error("Email address is missing");
        }
        await axios.post("/user/resend-email-otp", { email: formData.email });
        toast.success("Verification email has been resent.");
      } else {
        if (!formData.phoneNumber) {
          throw new Error("Phone number is missing");
        }
        if (!userIdForOtpModal) {
          throw new Error("User ID is missing");
        }
        await axios.post("/user/resend-sms-otp", {
          phoneNumber: formData.phoneNumber,
          userId: userIdForOtpModal,
        });
        showAlert(
          "Resend SMS",
          "Verification code has been sent to your phone.",
        );
      }
      return true;
    } catch (err) {
      showAlert(
        "Error",
        err.response?.data?.message ||
          err.message ||
          "Unable to resend verification code. Please try again later.",
      );
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsLoading(true);
    const registrationData = {
      ...formData,
      isAdminRequest: false,
      role: selectedRole,
    };

    try {
      const response = await axios.post(
        "/user/register",
        { formData: registrationData },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.data.userExists && response.data.hasDefaultPassword) {
        setIsLoading(false);
        handleShowDefaultPasswordModal(formData.email);
        return;
      }

      if (response?.data?.error) {
        showAlert(
          "Registration Failed",
          response.data.message || "An error occurred",
        );
      } else {
        setUserData(response.data.data);
        setPhoneNumber(response.data.data.phoneNumber || formData.phoneNumber);
        setUserIdForOtpModal(response.data.data.userId || "");
        setIsOtpModalOpen(true);
      }
    } catch (error) {
      setIsLoading(false);

      if (
        error.response?.status === 409 &&
        error.response?.data?.isTemporaryGuest
      ) {
        handleShowDefaultPasswordModal(formData.email);
        return;
      }

      if (error.response?.status === 409) {
        showAlert("Registration Failed", "User Already Exists, Please Login");
        navigate(`/login?role=${selectedRole}`);
        return;
      }

      showAlert(
        "Registration Failed",
        error.response?.data?.message ||
          "An error occurred during registration",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    clearPaidEventSession();
    setPaidEventData(null);
    navigate("/onboarding");
  };

  const handleSocialAuth = (provider) => {
    console.log(`${provider} authentication initiated`);
  };

  const handlePasswordSentContinue = () => {
    setShowPasswordSentModal(false);

    if (selectedRole) {
      localStorage.setItem("pendingLoginRole", selectedRole);
      sessionStorage.setItem("currentLoginRole", selectedRole);
    }

    navigate("/login", {
      state: {
        email: defaultPasswordEmail.toLowerCase().trim(),
        prefillEmail: "true",
        role: selectedRole,
        from: fromState,
      },
    });
  };

  return (
    <div className="sign-up-container">
      <div className="sign-up-img-container">
        <img
          src="/images/onboarding-page-image.webp"
          alt="NimbleBash celebration"
          className="sign-up-celebration-img"
        />
      </div>

      <div className="sign-up-content">
        <div className="sign-up-form-wrapper">
          <div className="sign-up-logo-container">
            <h1 className="sign-up-logo-wrapper">
              <img src={logoImage2} alt="Logo" className="login-logo" />
            </h1>
          </div>

          <div className="sign-up-form-container">
            <h1 className="sign-up-form-title">Create Account</h1>

            {paidEventData && (
              <div className="sign-up-pending-interest-banner">
                <div className="sign-up-banner-icon">🎉</div>
                <div className="sign-up-banner-content">
                  <h4>Complete signup to register for your event!</h4>
                  <p>
                    Event: <strong>{paidEventData.eventTitle}</strong>
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="sign-up-form">
              <div className="sign-up-name-row">
                <div className="sign-up-form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    className={
                      touchedFields.firstName && formErrors.firstName
                        ? "sign-up-input-error"
                        : ""
                    }
                    placeholder="Enter First Name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("firstName")}
                  />
                </div>

                <div className="sign-up-form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    className={
                      touchedFields.lastName && formErrors.lastName
                        ? "sign-up-input-error"
                        : ""
                    }
                    placeholder="Enter Last Name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("lastName")}
                  />
                </div>
              </div>

              <div className="sign-up-form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  className={
                    touchedFields.email && formErrors.email
                      ? "sign-up-input-error"
                      : ""
                  }
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onFocus={() => handleFieldFocus("email")}
                />
              </div>

              <div className="sign-up-form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <div
                  className={`sign-up-phone-input-container ${
                    touchedFields.phoneNumber && formErrors.phoneNumber
                      ? "sign-up-phone-input-error"
                      : ""
                  }`}
                >
                  <select
                    className="sign-up-country-code"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+234">+234</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+91">+91</option>
                  </select>
                  <input
                    type="tel"
                    id="phoneNumber"
                    placeholder="801 234 5678"
                    maxLength={12}
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("phoneNumber")}
                  />
                </div>
              </div>

              <div className="sign-up-form-group">
                <label htmlFor="password">Password</label>
                <div
                  className={`sign-up-password-container ${
                    touchedFields.password && formErrors.password
                      ? "sign-up-input-error"
                      : ""
                  }`}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("password")}
                    onBlur={() => handleFieldBlur("password")}
                  />
                  <button
                    type="button"
                    className="sign-up-eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEye className="sign-up-eye-icon" />
                    ) : (
                      <FaEyeSlash className="sign-up-eye-icon" />
                    )}
                  </button>
                </div>
              </div>

              {passwordFocused && formData.password !== "" && (
                <div className="sign-up-requirements-container">
                  <div className="sign-up-requirements-row">
                    <div
                      className={`sign-up-requirement-tag ${
                        passwordRequirements.oneSpecialChar
                          ? "sign-up-active"
                          : "sign-up-error"
                      }`}
                    >
                      One Special Character
                    </div>
                    <div
                      className={`sign-up-requirement-tag ${
                        passwordRequirements.oneNumber
                          ? "sign-up-active"
                          : "sign-up-error"
                      }`}
                    >
                      One Number
                    </div>
                    <div
                      className={`sign-up-requirement-tag ${
                        passwordRequirements.minLength
                          ? "sign-up-active"
                          : "sign-up-error"
                      }`}
                    >
                      8 Character Min
                    </div>
                    <div
                      className={`sign-up-requirement-tag ${
                        passwordRequirements.oneUppercase
                          ? "sign-up-active"
                          : "sign-up-error"
                      }`}
                    >
                      One Uppercase
                    </div>
                    <div
                      className={`sign-up-requirement-tag ${
                        passwordRequirements.oneLowercase
                          ? "sign-up-active"
                          : "sign-up-error"
                      }`}
                    >
                      One Lowercase
                    </div>
                  </div>
                </div>
              )}
            </form>

            {/* <div className="sign-up-social-login-container">
              <p className="sign-up-continue-with-text">Or Continue with</p>

              <div className="sign-up-social-buttons">
                <button className="sign-up-social-button sign-up-google-button">
                  <svg
                    className="sign-up-google-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Google</span>
                </button>

                <button className="sign-up-social-button sign-up-apple-button">
                  <FaApple className="sign-up-apple-icon" />
                  <span>Apple</span>
                </button>

                <button className="sign-up-social-button sign-up-facebook-button">
                  <svg
                    className="sign-up-facebook-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                      fill="#1877F2"
                    />
                  </svg>
                  <span>Facebook</span>
                </button>
              </div>
            </div> */}

            <div className="sign-up-signin-container">
              <p className="sign-up-login-text">
                Already have an account?{" "}
                <Link
                  to={`/login?role=${selectedRole}`}
                  state={{ from: fromState, role: selectedRole }}
                  className="sign-up-login-link"
                >
                  Login
                </Link>
              </p>
            </div>

            <div className="sign-up-action-buttons">
              <button
                type="button"
                className="sign-up-login-back-btn"
                onClick={handleBack}
              >
                Back
              </button>

              <button
                type="button"
                className={`sign-up-create-account-btn ${
                  !isFormValid ? "sign-up-disabled" : ""
                }`}
                disabled={!isFormValid || isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <OtpModal
        visible={isOtpModalOpen}
        email={formData.email}
        phoneNumber={phoneNumber || formData.phoneNumber}
        userId={userIdForOtpModal}
        selectedRole={selectedRole}
        method="email"
        onClose={() => setIsOtpModalOpen(false)}
        onVerify={handleOtpVerify}
        onResend={handleResendOtp}
        onEditEmail={() => setIsOtpModalOpen(false)}
        onEditPhone={() => setIsOtpModalOpen(false)}
        error={inviteError}
        pendingInterestData={paidEventData}
      />

      <WelcomeModal
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
        userName={userName}
        inviteCode={inviteCode}
        hostId={hostId}
        role={selectedRole}
        userData={userData}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
        pendingInterestData={paidEventData}
        buttons={{
          primary:
            selectedRole === "guest" || selectedRole === "usher"
              ? "Proceed to Events"
              : "Create Event",
          primaryAction: () =>
            handleWelcomeComplete(
              selectedRole === "guest" || selectedRole === "usher"
                ? "joinEvent"
                : "createEvent",
            ),
          secondary: selectedRole === "host" ? "Home" : null,
          secondaryAction: () => {
            setIsWelcomeModalOpen(false);
            setIsProcessing(false);
          },
        }}
      />

      <EventRegistrationSuccessModal
        isOpen={isSuccessModalOpen}
        eventTitle={registeredEventData?.eventTitle || ""}
        onViewEvent={handleViewEvent}
        onExplore={handleExploreEvents}
      />

      <PasswordSentModal
        isOpen={showPasswordSentModal}
        onContinue={handlePasswordSentContinue}
      />

      {showDefaultPasswordModal && (
        <div className="default-password-modal-overlay">
          <div className="default-password-modal">
            <h2>Email Already Used</h2>
            <p>
              We noticed you previously showed interest in an event using this
              email. To continue, we can send you a one-time password to
              complete your account setup.
            </p>
            <div className="default-password-modal-buttons">
              <button
                className="default-password-cancel-btn"
                onClick={handleCancelDefaultPassword}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="default-password-continue-btn"
                onClick={handleSendDefaultPassword}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;